/**
 * Checks to see if the value is null or udefined
 *
 * @export
 * @param {*} value Value to check
 * @returns {boolean} true if it's null or udefined else false
 */
export function isNullOrUndefined<T>(value: T | null | undefined): value is null | undefined {
	return value === null || isUndefined(value);
}

/**
 * Will validate the passed parameter against isNullOrUndefined, isObjectAndEmpty, isArrayAndEmpty, isStringAndEmpty
 *
 * @export
 * @param {(any[] | undefined)} collection
 * @returns {boolean}
 */
export function isNullOrEmpty(value: any): value is null | undefined {
	return isNullOrUndefined(value) || isStringAndEmpty(value) || isArrayAndEmpty(value) || isObjectAndEmpty(value);
}

export function isUndefined<T>(value: T | undefined): value is undefined {
	return value === void 0;
}

/**
 * Will return a boolean indicating wether the passed variable is an object
 *
 * @export
 * @param {any} obj
 * @returns {boolean}
 */
export function isObject(obj): obj is object {
	return typeof obj === 'object' && !Array.isArray(obj);
}

/**
 * Validates if the parameter is of type string and if it is null or whitespace
 *
 * @export
 * @param {any} value
 * @returns {boolean}
 */
export function isStringAndEmpty(value): boolean {
	return typeof value === 'string' && isNullOrWhitespace(value);
}

/**
 * Checks if a string is null or whitespace.
 *
 * @export
 * @param {string} value to check if the value is null or whitespace
 * @returns {boolean} true if it's null, undefined, empty or whitespace else false
 */
export function isNullOrWhitespace(value: string): boolean {
	const type = typeof value;
	if (type === 'boolean' || type === 'number') {
		value = value.toString();
	}
	return (value || '').trim() === '';
}

/**
 * Will validate if the parameter is of type array and if it is empty
 *
 * @export
 * @param {any} array
 * @returns {boolean}
 */
export function isArrayAndEmpty(array): boolean {
	return !isNullOrUndefined(array) && Array.isArray(array) && array.length === 0;
}

/**
 * Will validate if the passed parameter is of type object and has no properties defined
 *
 * @param {{}} obj
 * @returns {boolean}
 */
export function isObjectAndEmpty(obj?: Record<string, any> | null): boolean {
	if (!isObject(obj)) {
		return false;
	}

	for (const prop in obj) {
		if (obj[prop]) {
			return false;
		}
	}

	return JSON.stringify(obj) === JSON.stringify({});
}
/**
 * Will validate if the passed parameter is NaN
 *
 * @param {any} value
 * @returns {boolean}
 */
export function isNaN(value): boolean {
	if (value instanceof Number) {
		const numberValue = value.valueOf();
		return Number.isNaN(numberValue);
	}
	return Number.isNaN(value);
}
/**
 * Will validate if the passed parameter is a number
 *
 * @param {any} value
 * @returns {boolean}
 */
export function isNumber(value: any): value is number {
	return Number(value) === parseFloat(value) && typeof value === 'number' && !Number.isNaN(value);
}
/**
 * Will validate if the passed parameter can be a number
 *
 * @param {any} value
 * @returns {boolean}
 */
export function canBeNumber(value: any): boolean {
	return Number(value) === parseFloat(value) && !Number.isNaN(value);
}
/**
 * Will validate if the passed parameter is a date
 *
 * @param {any} value
 * @returns {boolean}
 */
export function isDate(value): boolean {
	return value instanceof Date;
}
/**
 * Will return a boolean indicating whether the passed string is a valid base64 image
 *
 * @export
 * @param {any} obj
 * @returns {boolean}
 */
export function isBase64File(value?: string | null): boolean {
	value = value && value.trim();
	const base64Regex =
		/^data:([a-z]+\/[a-z0-9-+.]+(;[a-z-]+=[a-z0-9-]+)?)?(;base64)?,([a-z0-9!$&',()*+;=\-._~:@/?%\s]*)$/i;
	return base64Regex.test(value);
}
